import type { BilingualName, BilingualText, BilingualAddress } from './common';
import { CommunicationLanguage } from './auth';
import { InstitutionCommittee } from './committee';

/** Budget-based categories per ICOM PDF (annual operating budget). */
export enum InstitutionMembershipCategory {
  ActiveI = 1,      // < €30,000
  ActiveII = 2,     // €30,000–€100,000
  ActiveIII = 3,    // €100,000–€1,000,000
  ActiveIV = 4,     // €1,000,000–€5,000,000
  ActiveV = 5,      // €5,000,000–€10,000,000
  ActiveVI = 6,     // > €10,000,000
  Benefactor = 7,   // no voting rights
}

export enum AdministrativeStatus {
  Public = 1,
  Private = 2,
  Mixed = 3,
}

export enum InstitutionContactPreference {
  Primary = 1,
  Secondary = 2,
}

/**
 * API shape for institution: GET /institutions/me and PUT /institutions/me.
 * Nested bilingual types align with backend InstitutionDto.
 */
export interface InstitutionDto {
  id: string;
  institutionName: BilingualText;
  institutionNameDisplay?: string;
  foundationDate?: string;
  website?: string;
  /** Backend Title enum; collected at registration. */
  directorTitle?: number;
  directorDateOfBirth?: string;
  directorName?: BilingualName;
  directorEmail?: string;
  directorPhone?: string;
  primaryAddress?: BilingualAddress;
  institutionPhone?: string;
  institutionEmail?: string;
  secondaryAddress?: BilingualAddress;
  icomRepresentativeName?: BilingualName;
  icomRepresentativeTitle?: number;
  icomRepresentativeDateOfBirth?: string;
  icomRepresentativePosition?: string;
  icomRepresentativeEmail?: string;
  icomRepresentativePhone?: string;
  icomContactPreference?: InstitutionContactPreference;
  icomRepresentativeCommunicationLanguage?: CommunicationLanguage | number;
  membershipCategory: InstitutionMembershipCategory;
  numberOfMemberCards: number;
  institutionType?: string[] | null;
  administrativeStatus?: AdministrativeStatus;
  collectionTypes?: string[] | null;
  registrationYear: number;
  contactPersonLastName?: string;
  contactPersonFirstName?: string;
  contactPersonPhone?: string;
  contactPersonEmail?: string;
  importProblems?: string;
  sourceRecordKey?: string;
  createdAt?: string;
  updatedAt?: string;
  committees?: InstitutionCommittee[];
  /** Whether the linked user account can log in (admin views). */
  isActive?: boolean;
}

/** Display name for institution (en ?? gr); use for list and profile. */
export function getInstitutionDisplayName(inst: InstitutionDto): string {
  return inst.institutionNameDisplay ?? inst.institutionName?.en ?? inst.institutionName?.gr ?? '';
}

/** Card allocation from register budget category — source of truth. */
export function memberCardsForCategory(category: InstitutionMembershipCategory): number {
  switch (category) {
    case InstitutionMembershipCategory.ActiveI:
      return 3;
    case InstitutionMembershipCategory.ActiveII:
      return 4;
    case InstitutionMembershipCategory.ActiveIII:
      return 5;
    case InstitutionMembershipCategory.ActiveIV:
      return 6;
    case InstitutionMembershipCategory.ActiveV:
      return 7;
    case InstitutionMembershipCategory.ActiveVI:
    case InstitutionMembershipCategory.Benefactor:
      return 8;
    default:
      return 3;
  }
}

export function normalizeCommunicationLanguage(
  value?: CommunicationLanguage | number | string | null
): CommunicationLanguage | undefined {
  if (value == null || value === '') return undefined;
  if (
    value === CommunicationLanguage.English ||
    value === 1 ||
    value === '1'
  ) {
    return CommunicationLanguage.English;
  }
  if (value === CommunicationLanguage.Greek || value === 2 || value === '2') {
    return CommunicationLanguage.Greek;
  }
  if (value === CommunicationLanguage.French || value === 3 || value === '3') {
    return CommunicationLanguage.French;
  }
  if (value === CommunicationLanguage.Spanish || value === 4 || value === '4') {
    return CommunicationLanguage.Spanish;
  }
  if (typeof value === 'string') return value as CommunicationLanguage;
  return undefined;
}

/** Flat shape for list/display; prefer InstitutionDto for profile/API. */
export interface Institution {
  id: string;
  institutionName: string;
  institutionNameEn?: string;
  institutionNameGr?: string;
  foundationDate?: string;
  website?: string;
  directorFirstName?: string;
  directorLastName?: string;
  directorEmail?: string;
  directorPhone?: string;
  institutionAddress?: string;
  institutionPostalCode?: string;
  institutionCity?: string;
  institutionCountry?: string;
  institutionPhone?: string;
  institutionEmail?: string;
  secondaryAddress?: string;
  secondaryPostalCode?: string;
  secondaryCity?: string;
  secondaryCountry?: string;
  icomRepresentativeLastName?: string;
  icomRepresentativeFirstName?: string;
  icomRepresentativePosition?: string;
  icomRepresentativeEmail?: string;
  icomRepresentativePhone?: string;
  icomContactPreference?: InstitutionContactPreference;
  membershipCategory: InstitutionMembershipCategory;
  numberOfMemberCards: number;
  institutionType?: string[];
  administrativeStatus?: AdministrativeStatus;
  collectionTypes?: string[];
  registrationYear: number;
  contactPersonLastName?: string;
  contactPersonFirstName?: string;
  contactPersonPhone?: string;
  contactPersonEmail?: string;
  createdAt: string;
  updatedAt: string;
  committees?: InstitutionCommittee[];
}

export interface InstitutionFilters {
  name?: string;
  category?: InstitutionMembershipCategory;
  region?: string;
  status?: boolean;
  search?: string;
}
